import FooterPanel from "../../layout/FooterPanel";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { IconButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import React from "react";
import { ObjectPanelTabs, TableUpdateStrategy } from "./ObjectPanelComponent";
import { IContextualMenuItem } from "@fluentui/react";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { selectUserFunctions } from "../../../redux/feature/settings/configurations-slice";
import { useAppSelector } from "../../../redux/hooks/hooks";
import { UserFunction } from "../../../api/models/user-function";
import { useWindowSize } from "../../../utils/helpers/useWindowSizeHook";

interface FooterPanelObjectProps {
  isEdit: boolean;
  isLocked?: boolean;
  selectedKey: number;
  tabsScrollView: ObjectPanelTabs[];
  onSaveCoObject: () => void;
  onUnlockObject: () => void;
  onNavigate: (key: string) => void;
  goToTabIndex: (index) => void;
  isInsertBtnEnabled: boolean;
  isInsertBtnVisible: boolean;
  isNextBtnEnabled: boolean;
  isNextBtnVisible: boolean;
  isSaveEnabled: boolean;
  isSaveVisible: boolean;
  isUpdateEnabled: boolean;
  isUpdateVisible: boolean;
  isDeleteEnabled: boolean;
  onDelete: () => void;
  refreshClickHandler: (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => void;
  isFileExcelExisting?: boolean;
  isOldSheetOrAreaSelected?: boolean;
  setTableUpdateType: (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => void;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
  isObjectTableOrChart: boolean;
  isComponentInstance: boolean;
  isLoadingSheetAndArea?: boolean;
  isSheetChartAndAreaToHide?: boolean;
}

export const FooterPanelObject = (props: FooterPanelObjectProps) => {
  const {
    isEdit,
    isLocked,
    isComponentInstance,
    selectedKey,
    tabsScrollView,
    onSaveCoObject,
    onNavigate,
    onUnlockObject,
    isInsertBtnEnabled,
    isInsertBtnVisible,
    isNextBtnEnabled,
    isNextBtnVisible,
    isSaveEnabled,
    isSaveVisible,
    isUpdateVisible,
    isUpdateEnabled,
    isDeleteEnabled,
    onDelete,
    refreshClickHandler,
    isFileExcelExisting,
    isOldSheetOrAreaSelected,
    tableUpdateType,
    isObjectTableOrChart,
    setTableUpdateType,
  } = props;

  const { width } = useWindowSize();

  const tableUpdateOptions: IContextualMenuItem[] = [
    {
      key: "update",
      text: formatMessage("cof.base.update"),
      depthLevel: "SHALLOWEST",
    },
    {
      key: "fullReset",
      text: formatMessage("cof.updates.fullreset"),
      depthLevel: "DEEPEST",
    },
  ];

  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  let notAuthorizedUserForLockObject = isNotAuthorizedUser(UserFunctionEnum.MENU_LOCK_OBJECT, userFunctions);

  if (isEdit)
    return (
      <FooterPanel isLocked={isLocked}>
        {isLocked ? (
          <div style={{ float: "right" }}>
            {formatMessage("cof.editobject.unlock.info")}
            <PrimaryButton
              id="unlock_button"
              disabled={notAuthorizedUserForLockObject}
              text={formatMessage("cof.base.unlock").toUpperCase()}
              onClick={onUnlockObject}
              style={{ marginLeft: "5px" }}
            />
          </div>
        ) : (
          <PrimaryButton
            id="delete_button"
            disabled={!isDeleteEnabled}
            text={formatMessage("cof.base.delete").toUpperCase()}
            onClick={() => onDelete()}
            className={ButtonVariants.red_filled}
            style={{ float: "left" }}
          />
        )}
        {isSaveVisible && !isLocked && (
          <PrimaryButton
            id="save_button"
            disabled={!isSaveEnabled || !isFileExcelExisting || props.isSheetChartAndAreaToHide}
            text={formatMessage("cof.base.save")}
            onClick={onSaveCoObject}
            style={{ float: "right" }}
          />
        )}
        {isUpdateVisible && !isLocked && (
          <PrimaryButton
            id="update_button"
            disabled={
              !isUpdateEnabled ||
              !isFileExcelExisting ||
              isOldSheetOrAreaSelected ||
              props.isLoadingSheetAndArea ||
              props.isSheetChartAndAreaToHide
            }
            className={ButtonVariants.primary}
            styles={{
              label: {
                whiteSpace: "nowrap",
              },
              root: { float: "right" },
              splitButtonContainer: { float: "right" },
            }}
            onClick={() => {
              refreshClickHandler(props.tableUpdateType);
            }}
            text={tableUpdateOptions.find((opt) => tableUpdateType === opt.key).text}
            split
            menuProps={
              isObjectTableOrChart && {
                items: tableUpdateOptions,
                isBeakVisible: false,
                onItemClick: (_, opt) => {
                  const newStrategyType = opt.key as TableUpdateStrategy["typeUpdate"];
                  setTableUpdateType(newStrategyType);
                },
              }
            }
          />
        )}
      </FooterPanel>
    );

  return (
    <FooterPanel
      scrollViewNavigationProps={
        tabsScrollView.length > 1
          ? {
              activeLink: selectedKey.toString(),
              items: tabsScrollView,
              isClickEnabled: true,
              onClickScrollView: (_, index) => {
                props.goToTabIndex(index);
              },
            }
          : undefined
      }
    >
      {selectedKey !== 0 && !isComponentInstance && (
        <>
          <PrimaryButton
            id="previous_button"
            text={formatMessage("cof.base.previous")}
            onClick={() => onNavigate("PREV")}
            className="ms-hiddenSm"
          />
          <IconButton
            id="previous_button"
            title={formatMessage("cof.base.previous")}
            onClick={() => onNavigate("PREV")}
            iconProps={{ iconName: "ChevronLeft" }}
            className="ms-hiddenMdUp"
          />
        </>
      )}
      {isInsertBtnVisible && (
        <PrimaryButton
          id="insert_button"
          disabled={!isInsertBtnEnabled}
          text={formatMessage("cof.base.insert")}
          onClick={onSaveCoObject}
          style={{ float: "right", marginLeft: "10px" }}
          data-testid={"insert"}
        />
      )}
      {isNextBtnVisible && (width >= 350 || !isInsertBtnVisible) && (
        <>
          <PrimaryButton
            id="next_button"
            disabled={!isNextBtnEnabled}
            text={formatMessage("cof.base.next")}
            onClick={() => onNavigate("NEXT")}
            style={{ float: "right" }}
            data-testid={"next"}
            className="ms-hiddenSm"
          />
          <IconButton
            id="next_button"
            disabled={!isNextBtnEnabled}
            text={formatMessage("cof.base.next")}
            onClick={() => onNavigate("NEXT")}
            data-testid={"next"}
            styles={{
              root: { float: "right", marginLeft: "10px" },
            }}
            iconProps={{ iconName: "ChevronRight" }}
            className="ms-hiddenMdUp"
          />
        </>
      )}
    </FooterPanel>
  );
};
